import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import { 
  ChevronDownIcon, 
  ChevronRightIcon, 
  UserIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const TeamHierarchy = () => {
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState(user?.id || null);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    fetchAllUsers();
    if (selectedUserId) {
      fetchHierarchy(selectedUserId);
    }
  }, [selectedUserId]);

  const fetchAllUsers = async () => {
    try {
      // Fetch all users without pagination limit
      const response = await api.get('/users', {
        params: {
          limit: 10000, // Large limit to get all users
          status: 'Active' // Only active users
        }
      });
      setAllUsers(response.data.users || []);
      console.log(`Fetched ${response.data.users?.length || 0} users for Team Hierarchy`);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users list');
    }
  };

  const fetchHierarchy = async (userId) => {
    setLoading(true);
    try {
      const response = await api.get(`/managers/hierarchy/${userId}`);
      setHierarchyData(response.data);
      
      // Auto-expand first level nodes
      const newExpanded = new Set();
      newExpanded.add(userId);
      setExpandedNodes(newExpanded);
    } catch (error) {
      console.error('Error fetching hierarchy:', error);
      toast.error('Failed to fetch team hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Count all subordinates recursively
  const countAllSubordinates = (userData) => {
    if (!userData.subordinates || userData.subordinates.length === 0) {
      return 0;
    }
    
    let count = userData.subordinates.length;
    userData.subordinates.forEach(sub => {
      count += countAllSubordinates(sub);
    });
    
    return count;
  };

  const renderUserCard = (userData, isCurrentUser = false, level = 0) => {
    const bgColor = isCurrentUser 
      ? 'bg-blue-50 border-blue-400' 
      : level === 0 
        ? 'bg-purple-50 border-purple-300'
        : 'bg-white border-gray-200';
    
    const textColor = isCurrentUser ? 'text-blue-900' : 'text-gray-900';
    const totalSubordinates = countAllSubordinates(userData);

    return (
      <div className={`${bgColor} border-2 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`p-2 rounded-full ${isCurrentUser ? 'bg-blue-200' : 'bg-gray-200'}`}>
              <UserIcon className="w-6 h-6 text-gray-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-2">
                <h3 className={`font-semibold text-lg ${textColor}`}>
                  {userData.name}
                </h3>
                {isCurrentUser && (
                  <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full font-semibold">You</span>
                )}
                {userData.isTeamLead && (
                  <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded-full font-semibold">Team Lead</span>
                )}
                {totalSubordinates > 0 && (
                  <span className="inline-flex items-center text-xs bg-green-500 text-white px-3 py-1 rounded-full font-bold shadow-md">
                    <UserGroupIcon className="w-3 h-3 mr-1" />
                    {totalSubordinates} {totalSubordinates === 1 ? 'Person' : 'People'}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600 space-y-1 mt-2">
                <div className="flex items-center space-x-1">
                  <EnvelopeIcon className="w-4 h-4" />
                  <span>{userData.email}</span>
                </div>
                {userData.department && (
                  <div className="flex items-center space-x-1">
                    <BuildingOfficeIcon className="w-4 h-4" />
                    <span>{userData.department}</span>
                  </div>
                )}
                {userData.relationship && (
                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                    {userData.relationship}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUpperChain = (chain, level = 0) => {
    if (!chain || chain.length === 0) return null;

    return (
      <div className="space-y-4">
        {chain.map((manager, index) => (
          <div key={manager.id} className="relative">
            <div className="flex items-start space-x-4">
              {/* Vertical line */}
              {level > 0 && (
                <div className="flex flex-col items-center">
                  <div className="w-0.5 h-full bg-purple-300"></div>
                </div>
              )}
              
              <div className="flex-1">
                {renderUserCard(manager, false, level)}
                
                {/* Recursively render manager's managers */}
                {manager.managers && manager.managers.length > 0 && (
                  <div className="ml-8 mt-4">
                    {renderUpperChain(manager.managers, level + 1)}
                  </div>
                )}
              </div>
            </div>
            
            {/* Arrow pointing down to next level */}
            {index === chain.length - 1 && (
              <div className="flex justify-center mt-2 mb-2">
                <ChevronUpIcon className="w-6 h-6 text-purple-400" />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSubordinatesTree = (subordinates, parentId, level = 0) => {
    if (!subordinates || subordinates.length === 0) return null;

    return (
      <div className={`${level > 0 ? 'ml-8' : ''} space-y-3 mt-3`}>
        {subordinates.map((subordinate, index) => {
          const nodeId = `${parentId}-${subordinate.id}`;
          const isExpanded = expandedNodes.has(subordinate.id);
          const hasChildren = subordinate.subordinates && subordinate.subordinates.length > 0;

          return (
            <div key={subordinate.id} className="relative">
              {/* Connecting lines */}
              {level > 0 && (
                <>
                  <div className="absolute left-[-24px] top-6 w-6 h-0.5 bg-blue-300"></div>
                  {index < subordinates.length - 1 && (
                    <div className="absolute left-[-24px] top-6 bottom-0 w-0.5 bg-blue-300"></div>
                  )}
                </>
              )}

              <div className="flex items-start space-x-2">
                {hasChildren && (
                  <button
                    onClick={() => toggleNode(subordinate.id)}
                    className="mt-4 p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                )}
                {!hasChildren && <div className="w-7"></div>}
                
                <div className="flex-1">
                  {renderUserCard(subordinate, false, level + 1)}
                </div>
              </div>

              {/* Recursively render subordinates */}
              {hasChildren && isExpanded && (
                renderSubordinatesTree(subordinate.subordinates, subordinate.id, level + 1)
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const filteredUsers = allUsers.filter(u => 
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.officialEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
            <UserGroupIcon className="w-8 h-8" />
            <span>Team Hierarchy</span>
          </h1>
          <p className="text-gray-600 mt-2">
            View organizational structure and reporting relationships
          </p>
        </div>

        {/* User Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Select Employee
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">
                Showing {filteredUsers.length} of {allUsers.length} employees
              </span>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[300px]"
            >
              <option value="">Select a user...</option>
              {filteredUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} - {u.officialEmail}
                </option>
              ))}
            </select>
          </div>
          {filteredUsers.length === 0 && searchTerm && (
            <p className="text-sm text-gray-500 mt-2">No employees found matching "{searchTerm}"</p>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Hierarchy Display */}
        {!loading && hierarchyData && (
          <div className="space-y-8">
            {/* Upper Management Chain */}
            {hierarchyData.upperChain && hierarchyData.upperChain.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <ChevronUpIcon className="w-6 h-6 text-purple-600" />
                  <span>Management Chain</span>
                </h2>
                <div className="space-y-2">
                  {renderUpperChain(hierarchyData.upperChain)}
                </div>
              </div>
            )}

            {/* Current User */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <UserIcon className="w-6 h-6 text-blue-600" />
                <span>Selected Employee</span>
              </h2>
              {renderUserCard(hierarchyData.user, selectedUserId === user?.id)}
            </div>

            {/* Subordinates Tree */}
            {hierarchyData.subordinatesTree && hierarchyData.subordinatesTree.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                    <ChevronDownIcon className="w-6 h-6 text-blue-600" />
                    <span>Team Structure</span>
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full">
                      <UserIcon className="w-4 h-4 mr-1" />
                      <span className="text-sm font-bold">{hierarchyData.subordinatesTree.length} Direct</span>
                    </div>
                    <div className="flex items-center bg-green-100 text-green-800 px-3 py-1.5 rounded-full">
                      <UserGroupIcon className="w-4 h-4 mr-1" />
                      <span className="text-sm font-bold">
                        {hierarchyData.subordinatesTree.reduce((total, sub) => total + countAllSubordinates(sub), hierarchyData.subordinatesTree.length)} Total
                      </span>
                    </div>
                  </div>
                </div>
                {renderSubordinatesTree(hierarchyData.subordinatesTree, hierarchyData.user.id)}
              </div>
            )}

            {/* No subordinates message */}
            {(!hierarchyData.subordinatesTree || hierarchyData.subordinatesTree.length === 0) && (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">This employee has no direct reports</p>
              </div>
            )}
          </div>
        )}

        {/* No data selected */}
        {!loading && !hierarchyData && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <UserGroupIcon className="w-20 h-20 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Select an employee to view their hierarchy
            </h3>
            <p className="text-gray-500">
              Choose an employee from the dropdown above to see their management chain and team structure
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamHierarchy;

